// src/pages/Competitions/components/ClubRankingPanel.jsx

import React, { useMemo, useState } from "react";
import { FaMedal } from "react-icons/fa";
import { FiUsers } from "react-icons/fi";

/**
 * Barème du classement club.
 *
 * Important :
 * - total_points = points sportifs/techniques du combattant
 * - clubPoints = points calculés pour le classement des clubs
 *
 * Tu peux ajuster ce barème selon le règlement de ton tournoi.
 */
const CLUB_POINTS_RULES = {
  fighter: 1, // judoka ayant combattu
  victory: 1, // victoire
  ranking: {
    1: 10,
    2: 7,
    3: 5,
    4: 3,
    5: 1,
  },
};

const safeNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const parseRank = (value) => {
  if (value === null || value === undefined || value === "") return null;

  if (typeof value === "number") {
    return Number.isFinite(value) && value > 0 ? value : null;
  }

  const str = String(value).trim();
  const match = str.match(/\d+/);
  if (!match) return null;

  const rank = Number(match[0]);
  return Number.isFinite(rank) && rank > 0 ? rank : null;
};

const getClubName = (result) => {
  return (
    result?.club_competiteur ||
    result?.club ||
    result?.nom_club ||
    "Sans club"
  ).trim();
};

const hasCompetitorFought = (result, rank, victories, combatPoints) => {
  return (
    Boolean(result?.is_present) ||
    victories > 0 ||
    combatPoints > 0 ||
    rank !== null
  );
};

const ClubRankingPanel = ({ results = [] }) => {
  const [mode, setMode] = useState("fighters"); // "fighters" | "medals"

  const clubsData = useMemo(() => {
    const map = new Map();

    (results || []).forEach((r) => {
      if (!r) return;

      const clubName = getClubName(r);
      const rank = parseRank(r.classement_position);
      const victories = safeNumber(r.total_victoires, 0);
      const combatPoints = safeNumber(r.total_points, 0);

      const hasFought = hasCompetitorFought(
        r,
        rank,
        victories,
        combatPoints
      );

      if (!map.has(clubName)) {
        map.set(clubName, {
          club: clubName,
          fighters: 0,
          gold: 0,
          silver: 0,
          bronze: 0,
          victories: 0,

          // Ancien total : points techniques / points combat
          combatPoints: 0,

          // Nouveau total : points du challenge club
          clubPoints: 0,
        });
      }

      const rec = map.get(clubName);

      if (hasFought) {
        rec.fighters += 1;
        rec.clubPoints += CLUB_POINTS_RULES.fighter;
      }

      if (rank === 1) rec.gold += 1;
      else if (rank === 2) rec.silver += 1;
      else if (rank === 3) rec.bronze += 1;

      rec.victories += victories;
      rec.combatPoints += combatPoints;

      rec.clubPoints += victories * CLUB_POINTS_RULES.victory;
      rec.clubPoints += CLUB_POINTS_RULES.ranking[rank] || 0;
    });

    return Array.from(map.values()).map((rec) => ({
      ...rec,
      totalMedals: rec.gold + rec.silver + rec.bronze,
    }));
  }, [results]);

  const sorted = useMemo(() => {
    const arr = [...clubsData];

    if (mode === "fighters") {
      arr.sort((a, b) => {
        if (b.fighters !== a.fighters) return b.fighters - a.fighters;
        if (b.clubPoints !== a.clubPoints) return b.clubPoints - a.clubPoints;
        if (b.gold !== a.gold) return b.gold - a.gold;
        if (b.silver !== a.silver) return b.silver - a.silver;
        if (b.bronze !== a.bronze) return b.bronze - a.bronze;
        if (b.victories !== a.victories) return b.victories - a.victories;
        return a.club.localeCompare(b.club, "fr", { sensitivity: "base" });
      });
    } else {
      arr.sort((a, b) => {
        if (b.gold !== a.gold) return b.gold - a.gold;
        if (b.silver !== a.silver) return b.silver - a.silver;
        if (b.bronze !== a.bronze) return b.bronze - a.bronze;
        if (b.totalMedals !== a.totalMedals) {
          return b.totalMedals - a.totalMedals;
        }
        if (b.clubPoints !== a.clubPoints) return b.clubPoints - a.clubPoints;
        if (b.victories !== a.victories) return b.victories - a.victories;
        if (b.fighters !== a.fighters) return b.fighters - a.fighters;
        return a.club.localeCompare(b.club, "fr", { sensitivity: "base" });
      });
    }

    return arr;
  }, [clubsData, mode]);

  if (!results || results.length === 0) {
    return (
      <div className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
        Aucun résultat disponible pour calculer le classement clubs.
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1280px] rounded-2xl border border-gray-200/70 bg-white p-4 shadow-lg dark:border-slate-700/70 dark:bg-slate-900 sm:p-5">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-bold text-gray-900 dark:text-white sm:text-xl">
            <FaMedal className="text-yellow-500" />
            Classement des clubs
          </h2>

          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Les points affichés sont des points de classement club, pas les
            points techniques marqués en combat.
          </p>
        </div>

        <div className="inline-flex w-full rounded-xl bg-gray-100 p-1 dark:bg-slate-800 sm:w-auto">
          <button
            type="button"
            onClick={() => setMode("fighters")}
            className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition sm:flex-none sm:text-sm ${
              mode === "fighters"
                ? "bg-blue-600 text-white shadow-sm"
                : "text-gray-700 hover:bg-white/70 dark:text-gray-200 dark:hover:bg-slate-700"
            }`}
          >
            <FiUsers />
            Par judokas
          </button>

          <button
            type="button"
            onClick={() => setMode("medals")}
            className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition sm:flex-none sm:text-sm ${
              mode === "medals"
                ? "bg-blue-600 text-white shadow-sm"
                : "text-gray-700 hover:bg-white/70 dark:text-gray-200 dark:hover:bg-slate-700"
            }`}
          >
            <FaMedal />
            Par médailles
          </button>
        </div>
      </div>

      <div className="mb-4 grid grid-cols-1 gap-2 text-xs text-gray-600 dark:text-gray-300 sm:grid-cols-3">
        <div className="rounded-xl bg-gray-50 px-3 py-2 dark:bg-slate-800/70">
          <span className="font-bold">Participation :</span>{" "}
          {CLUB_POINTS_RULES.fighter} pt / judoka ayant combattu
        </div>

        <div className="rounded-xl bg-gray-50 px-3 py-2 dark:bg-slate-800/70">
          <span className="font-bold">Victoire :</span>{" "}
          {CLUB_POINTS_RULES.victory} pt / victoire
        </div>

        <div className="rounded-xl bg-gray-50 px-3 py-2 dark:bg-slate-800/70">
          <span className="font-bold">Podium :</span> 1er 10 pts · 2e 7 pts ·
          3e 5 pts
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse text-sm">
          <thead>
            <tr className="bg-gray-50 text-xs uppercase tracking-wide text-gray-600 dark:bg-slate-800/70 dark:text-gray-300">
              <th className="px-3 py-2 text-center">Rang</th>
              <th className="px-3 py-2 text-left">Club</th>
              <th className="px-3 py-2 text-center">
                Judokas{" "}
                <span className="hidden sm:inline">ayant combattu</span>
              </th>
              <th className="px-3 py-2 text-center">Or</th>
              <th className="px-3 py-2 text-center">Argent</th>
              <th className="px-3 py-2 text-center">Bronze</th>
              <th className="px-3 py-2 text-center">Total médailles</th>
              <th className="px-3 py-2 text-center">Victoires</th>
              <th className="px-3 py-2 text-center">Points club</th>
              <th className="px-3 py-2 text-center">Pts combat</th>
            </tr>
          </thead>

          <tbody>
            {sorted.map((club, index) => (
              <tr
                key={club.club}
                className="border-t border-gray-100 odd:bg-white even:bg-gray-50/60 dark:border-slate-800 dark:odd:bg-slate-900 dark:even:bg-slate-900/60"
              >
                <td className="px-3 py-2 text-center font-bold">
                  {index + 1}
                </td>

                <td className="px-3 py-2 text-left font-semibold text-gray-900 dark:text-white">
                  {club.club}
                </td>

                <td className="px-3 py-2 text-center">{club.fighters}</td>

                <td className="px-3 py-2 text-center font-semibold text-yellow-600 dark:text-yellow-300">
                  {club.gold}
                </td>

                <td className="px-3 py-2 text-center text-gray-500 dark:text-gray-200">
                  {club.silver}
                </td>

                <td className="px-3 py-2 text-center text-amber-800 dark:text-amber-300">
                  {club.bronze}
                </td>

                <td className="px-3 py-2 text-center font-semibold">
                  {club.totalMedals}
                </td>

                <td className="px-3 py-2 text-center">{club.victories}</td>

                <td className="px-3 py-2 text-center font-bold text-blue-700 dark:text-blue-300">
                  {club.clubPoints}
                </td>

                <td className="px-3 py-2 text-center text-gray-400 dark:text-gray-500">
                  {club.combatPoints}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ClubRankingPanel;